import {
  SLOT_H, START_H, END_H, TOTAL_S, MIN_DUR,
  DAYS_SHORT, DAYS_LONG, MONTHS, COLORS,
  slotToHHMM, genId, dateISO, esc, weekKey,
  hexToRgb, hexToUniformDarkText,
} from './config.js';
import { state, getWeekBlocks, setWeekBlocks, findBlock, patchBlock, removeBlock } from './state.js';
import {
  updateBlockAPI, deleteBlockAPI, deleteBlockFromCalendar,
  addExceptionAPI, updateBlockInCalendar, updateTaskAPI,
} from './api.js';
import { assignOverlapColumns, assignAllDayRows, findBlockEventOverlaps } from './layout.js';

// ══════════════════════════════════════════════════════════
// BUILD / REBUILD GRID
// ══════════════════════════════════════════════════════════
export function buildGrid() {
  buildHeader();
  buildAllDayLane();
  buildGutter();
  buildColumns();
}

// ── All-day events lane (read-only, below the day header) ──
export function buildAllDayLane() {
  const cal = document.getElementById('calendar');
  const header = document.getElementById('cal-header');
  if (!cal || !header) return;

  document.getElementById('allday-lane')?.remove();

  const lane = document.createElement('div');
  lane.id = 'allday-lane';
  let html = '<div class="allday-gutter">tot el dia</div>';
  for (let i = 0; i < 7; i++) {
    html += `<div class="allday-cell${i >= 5 ? ' weekend-col' : ''}" data-day="${i}"></div>`;
  }
  lane.innerHTML = html;
  header.insertAdjacentElement('afterend', lane);

  // Les barres es posicionen mesurant les cel·les: si canvia l'ample, recol·locar.
  let lastW = 0;
  new ResizeObserver(([entry]) => {
    const w = Math.round(entry.contentRect.width);
    if (w === lastW) return;
    lastW = w;
    requestAnimationFrame(renderAllDayEvents);
  }).observe(lane);

  renderAllDayEvents();
}

export function renderAllDayEvents() {
  const lane = document.getElementById('allday-lane');
  if (!lane) return;
  lane.querySelectorAll('.allday-bar').forEach(b => b.remove());

  const dayView = isDayView();
  lane.querySelectorAll('.allday-cell').forEach(c => {
    c.classList.toggle('focus-day', Number(c.dataset.day) === state.focusDay);
  });

  // Normalize day span (backend clamps startDay/endDay to 0–6).
  let evs = state.allDayEvents.map(ev => {
    const startDay = ev.startDay ?? ev.day ?? 0;
    const endDay   = ev.endDay ?? startDay;
    return { ...ev, startDay, endDay };
  });
  // Vista dia: només els events que cobreixen el dia visible, un per fila.
  if (dayView) evs = evs.filter(e => e.startDay <= state.focusDay && state.focusDay <= e.endDay);

  const rows = dayView
    ? Object.fromEntries(evs.map((e, i) => [e.id, i]))
    : assignAllDayRows(evs.map(e => ({ id: e.id, startDay: e.startDay, endDay: e.endDay })));
  const maxRow = Object.values(rows).reduce((m, r) => Math.max(m, r), -1);
  const ROW_H = 22; // px per stacked row
  const cells = [...lane.querySelectorAll('.allday-cell')];

  evs.forEach(ev => {
    let baseHex = ev.color || '#4285f4';
    if (baseHex.length === 4) {
      baseHex = '#' + baseHex[1]+baseHex[1]+baseHex[2]+baseHex[2]+baseHex[3]+baseHex[3];
    }
    const rgb       = hexToRgb(baseHex) || hexToRgb('#4285f4');
    const textColor = hexToUniformDarkText(baseHex);
    const row  = rows[ev.id] ?? 0;

    // Geometry measured from the day cells (columns can have different widths:
    // empty weekend days are narrower). In day view the bar spans the visible day.
    const first = cells[dayView ? state.focusDay : ev.startDay];
    const last  = cells[dayView ? state.focusDay : ev.endDay];
    if (!first || !last) return;
    const left  = first.offsetLeft;
    const width = last.offsetLeft + last.offsetWidth - left;

    const bar = document.createElement('div');
    bar.className = 'allday-bar';
    bar.style.cssText =
      `top:${row * ROW_H + 3}px;` +
      `left:${left + 3}px;width:${width - 6}px;` +
      `background:rgba(${rgb.r},${rgb.g},${rgb.b},0.14);` +
      `border-left:3px solid ${textColor};color:${textColor};`;
    bar.title = ev.title;
    bar.textContent = ev.title;
    lane.appendChild(bar);
  });

  // Grow the lane to fit stacked rows (CSS max-height caps it → scrolls).
  lane.style.minHeight = evs.length ? `${(maxRow + 1) * ROW_H + 6}px` : '';
}

export function buildHeader() {
  const hdr = document.getElementById('cal-header');
  hdr.querySelectorAll('.day-hcell').forEach(e => e.remove());

  const today = new Date(); today.setHours(0,0,0,0);

  for (let i = 0; i < 7; i++) {
    const d = new Date(state.currentMonday);
    d.setDate(d.getDate() + i);
    d.setHours(0, 0, 0, 0);
    const isToday = d.getTime() === today.getTime();

    const monthName = MONTHS[d.getMonth()];
    const cell = document.createElement('div');
    cell.className = 'day-hcell' + (isToday ? ' today' : '') + (i >= 5 ? ' weekend' : '')
      + (i === state.focusDay ? ' focus-day' : '');
    cell.dataset.day = i;
    cell.innerHTML = `
      <div class="dh-name">${DAYS_SHORT[i]}</div>
      <div class="dh-date-row">
        <span class="dh-date">${d.getDate()}</span>
        <span class="dh-month">${monthName}</span>
      </div>`;
    hdr.appendChild(cell);
  }

  updateNavLabel();
}

function isDayView() {
  return document.body.classList.contains('view-day');
}

// Etiqueta i tooltips de la navegació: rang de la setmana, o el dia visible.
function updateNavLabel() {
  const mon = state.currentMonday;
  let label;
  if (isDayView()) {
    const d = new Date(mon); d.setDate(d.getDate() + state.focusDay);
    label = `${DAYS_LONG[state.focusDay]} ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  } else {
    const end = new Date(mon); end.setDate(end.getDate() + 6);
    label = `${mon.getDate()} ${MONTHS[mon.getMonth()]} — ${end.getDate()} ${MONTHS[end.getMonth()]} ${end.getFullYear()}`;
  }
  document.getElementById('week-label').textContent = label;
  document.getElementById('btn-prev').title = isDayView() ? 'Dia anterior' : 'Setmana anterior';
  document.getElementById('btn-next').title = isDayView() ? 'Dia següent'  : 'Setmana següent';
}

// Vista dia: canvia el dia visible dins la setmana carregada (sense recarregar dades).
export function markFocusDay() {
  document.querySelectorAll('#cal-header .day-hcell, #days-area .day-col').forEach(el => {
    el.classList.toggle('focus-day', Number(el.dataset.day) === state.focusDay);
  });
  updateNavLabel();
  renderAllDayEvents();
}

export function buildGutter() {
  const g = document.getElementById('time-gutter');
  g.innerHTML = '';
  g.style.height = TOTAL_S * SLOT_H + 'px';

  for (let h = START_H; h <= END_H; h++) {
    const slot = (h - START_H) * 4;
    const lbl = document.createElement('div');
    lbl.className = 'hour-label';
    lbl.style.top = slot * SLOT_H + 'px';
    lbl.textContent = String(h).padStart(2,'0') + ':00';
    g.appendChild(lbl);
  }
}

export function buildColumns() {
  const area = document.getElementById('days-area');
  area.innerHTML = '';

  const todayMidnight = new Date(); todayMidnight.setHours(0,0,0,0);

  for (let i = 0; i < 7; i++) {
    const colDate = new Date(state.currentMonday); colDate.setDate(colDate.getDate() + i); colDate.setHours(0,0,0,0);
    const isToday = colDate.getTime() === todayMidnight.getTime();
    const col = document.createElement('div');
    col.className = 'day-col' + (isToday ? ' today-col' : '') + (i >= 5 ? ' weekend-col' : '')
      + (i === state.focusDay ? ' focus-day' : '');
    col.dataset.day = i;
    col.style.height = TOTAL_S * SLOT_H + 'px';

    // Grid lines
    for (let s = 0; s < TOTAL_S; s++) {
      const ln = document.createElement('div');
      ln.className = 'slot-line ' + (s % 4 === 0 ? 'is-hour' : s % 2 === 0 ? 'is-half' : 'is-qtr');
      ln.style.top = s * SLOT_H + 'px';
      col.appendChild(ln);
    }

    col.addEventListener('mousedown', onColMousedown);

    // Drag-from-sidebar drop target
    col.addEventListener('dragover', e => {
      if (!state.pendingTaskDrop) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    });
    col.addEventListener('drop', e => {
      if (!state.pendingTaskDrop) return;
      e.preventDefault();
      const task = state.pendingTaskDrop;
      state.pendingTaskDrop = null;

      const blockEl = e.target.closest?.('.block');
      if (blockEl) {
        const blockId = blockEl.dataset.id;
        const block = findBlock(blockId);
        if (block) {
          if (!task.block_id) {
            updateTaskAPI(task.id, { block_id: blockId }).then(() => {
              rerenderBlock(blockId);
              renderSidebarExternal();
            });
            _toast(`Tasca afegida a "${block.title}"`);
          }
          return;
        }
      }

      const colRect = col.getBoundingClientRect();
      const slot = Math.max(0, Math.min(TOTAL_S - 4, Math.floor((e.clientY - colRect.top) / SLOT_H)));
      const suggestedType = state.blockTypes.find(t =>
        task.priority === 'alta' ? t.label.toLowerCase().includes('focus') || t.label.toLowerCase().includes('deep') :
        task.priority === 'mitja' ? t.label.toLowerCase().includes('gestió') || t.label.toLowerCase().includes('gesti') :
        t.label.toLowerCase().includes('buffer') || t.label.toLowerCase().includes('reunió')
      ) || state.blockTypes[0];

      openCreateModalExternal(parseInt(col.dataset.day), slot, null, {
        title:   task.title,
        tipusId: suggestedType?.id || '',
        taskId:  task.id,
      });
    });

    area.appendChild(col);
  }

  renderAllBlocks();
  renderCalendarEvents();
  renderAllDayEvents();
  updateNowLine();
}

// ══════════════════════════════════════════════════════════
// RENDER BLOCKS
// ══════════════════════════════════════════════════════════
export function renderAllBlocks() {
  document.querySelectorAll('.block').forEach(e => e.remove());
  getWeekBlocks().forEach(renderBlock);
  updateWeekSummary();
  applyAllEventRails();
}

export function updateWeekSummary() {
  const el = document.getElementById('week-summary');
  if (!el) return;
  const blocks = getWeekBlocks();

  if (blocks.length === 0) {
    el.innerHTML = '<span class="summary-empty">Cap bloc planificat aquesta setmana</span>';
    return;
  }

  const slotsByTipus = {};
  let totalSlots = 0;
  blocks.forEach(b => {
    const t = b.tipusId || '__cap';
    slotsByTipus[t] = (slotsByTipus[t] || 0) + b.dur;
    totalSlots += b.dur;
  });

  const fmtH = slots => {
    const h = slots * 15 / 60;
    return h % 1 === 0 ? h + 'h' : h.toFixed(1) + 'h';
  };

  let html = '';
  state.blockTypes.forEach(t => {
    if (!slotsByTipus[t.id]) return;
    const colorId = (COLORS.find(c => c.id === t.color) ? t.color : 'sky');
    html += `<span class="summary-pill"><span class="sdot" style="background:var(--${colorId}-fg)"></span>${esc(t.label)} <strong>${fmtH(slotsByTipus[t.id])}</strong></span>`;
  });
  if (slotsByTipus['__cap']) {
    html += `<span class="summary-pill"><span class="sdot" style="background:var(--text-4)"></span>Sense tipus <strong>${fmtH(slotsByTipus['__cap'])}</strong></span>`;
  }

  html += '<div class="summary-sep"></div>';
  html += `<span class="summary-total">Total: <strong>${fmtH(totalSlots)}</strong></span>`;

  el.innerHTML = html;
}

export function renderBlock(b) {
  const col = document.querySelector(`.day-col[data-day="${b.day}"]`);
  if (!col) return;

  const tipusDef = state.blockTypes.find(t => t.id === (b.tipusId || b.tipus));
  const colorSrc = tipusDef?.color || b.color;
  const colorId  = COLORS.find(c => c.id === colorSrc) ? colorSrc : 'sky';
  const label    = tipusDef
    ? `${tipusDef.emoji ? tipusDef.emoji + ' ' : ''}${tipusDef.label}`
    : b.title;
  const startT = slotToHHMM(b.startSlot);
  const endT   = slotToHHMM(b.startSlot + b.dur);

  const el = document.createElement('div');
  el.className = 'block b-' + colorId;
  el.dataset.id = b.id;
  el.style.top    = b.startSlot * SLOT_H + 'px';
  el.style.height = b.dur * SLOT_H - 2 + 'px';

  const recurIconHtml = b.templateId ? `<div class="recur-icon">↻</div>` : '';

  el.innerHTML = `
    <div class="block-body">
      <div class="block-title">${esc(label)}</div>
      ${b.dur >= 3 ? `<div class="block-time-label">${startT}–${endT}</div>` : ''}
    </div>
    ${recurIconHtml}
    <div class="resize-handle"></div>
  `;

  el.querySelector('.block-body').addEventListener('mousedown', e => {
    e.stopPropagation();
    startBlockInteraction(e, b.id, 'move');
  });

  el.querySelector('.block-body').addEventListener('dblclick', e => {
    e.stopPropagation();
    openEditModalExternal(b.id);
  });

  el.querySelector('.resize-handle').addEventListener('mousedown', e => {
    e.stopPropagation();
    startBlockInteraction(e, b.id, 'resize');
  });

  col.appendChild(el);
  applyEventRails(b.day);
}

export function rerenderBlock(id) {
  document.querySelector(`.block[data-id="${id}"]`)?.remove();
  const b = findBlock(id);
  if (b) renderBlock(b);
  updateWeekSummary();
  applyAllEventRails();
  if (id === state.selectedBlockId) {
    document.querySelector(`.block[data-id="${id}"]`)?.classList.add('block-selected');
  }
}

// ══════════════════════════════════════════════════════════
// DELETE BLOCK (with recurring check)
// ══════════════════════════════════════════════════════════
export function handleDeleteBlock(id) {
  const b = findBlock(id);
  if (!b) return;

  if (b.templateId) {
    openChoiceModalExternal(
      'Eliminar bloc recurrent',
      'Vols eliminar només aquest dia o tots els futurs?',
      [
        {
          label: 'Eliminar només avui',
          action: () => {
            const mondayDate = new Date(weekKey(state.currentMonday) + 'T00:00:00');
            const isoDate = dateISO(mondayDate, b.day);
            state.exceptions[`${b.templateId}|${isoDate}`] = true;
            addExceptionAPI(b.templateId, isoDate).catch(() => {});
            const _recurCalEventId = b.calendarEventId;
            removeBlock(id);
            document.querySelector(`.block[data-id="${id}"]`)?.remove();
            applyEventRails(b.day);
            if (id === state.selectedBlockId) deselectBlockExternal();
            deleteBlockAPI(id).catch(() => {});
            if (_recurCalEventId) deleteBlockFromCalendar(_recurCalEventId).catch(() => {});
            _toast('Bloc eliminat (només avui)');
          },
          danger: false,
        },
        {
          label: 'Eliminar tots els futurs',
          action: () => {
            deleteTemplateExternal(b.templateId);
            renderAllBlocks();
            _toast('Plantilla i tots els blocs eliminats');
          },
          danger: true,
        },
      ]
    );
  } else {
    const _calEventId = b.calendarEventId;
    removeBlock(id);
    document.querySelector(`.block[data-id="${id}"]`)?.remove();
    applyEventRails(b.day);
    if (id === state.selectedBlockId) deselectBlockExternal();
    updateWeekSummary();
    deleteBlockAPI(id).catch(() => {});
    if (_calEventId) deleteBlockFromCalendar(_calEventId).catch(() => {});
    _toast('Bloc eliminat');
  }
}

// ══════════════════════════════════════════════════════════
// DRAG & DROP
// ══════════════════════════════════════════════════════════
export function startBlockInteraction(e, id, type) {
  if (e.button !== 0) return;
  e.preventDefault();

  const block = findBlock(id);
  if (!block) return;

  const el = document.querySelector(`.block[data-id="${id}"]`);
  if (!el) return;

  const elRect = el.getBoundingClientRect();
  const grabOffset = Math.max(0, Math.floor((e.clientY - elRect.top) / SLOT_H));

  state.pending = { id, x: e.clientX, y: e.clientY };

  state.drag = {
    type, id,
    origDay:   block.day,
    origStart: block.startSlot,
    origDur:   block.dur,
    mouseX0:   e.clientX,
    mouseY0:   e.clientY,
    grabSlotOffset: grabOffset,
    started: false,
    curSlot: block.startSlot,
    curDay:  block.day,
    curDur:  block.dur,
  };

  document.addEventListener('mousemove', onDragMove, { passive: true });
  document.addEventListener('mouseup',   onDragUp);
}

function onDragMove(e) {
  if (!state.drag) return;

  const dx = e.clientX - state.drag.mouseX0;
  const dy = e.clientY - state.drag.mouseY0;

  if (!state.drag.started && Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
  state.drag.started = true;
  if (state.pending) { state.pending.moved = true; }

  const el = document.querySelector(`.block[data-id="${state.drag.id}"]`);
  if (!el) return;

  if (state.drag.type === 'move') {
    const deltaSlots = Math.round(dy / SLOT_H);
    let newSlot = state.drag.origStart + deltaSlots;
    newSlot = Math.max(0, Math.min(TOTAL_S - state.drag.origDur, newSlot));

    const cols = Array.from(document.querySelectorAll('.day-col'));
    let newDay = state.drag.origDay;
    for (let i = 0; i < cols.length; i++) {
      const r = cols[i].getBoundingClientRect();
      if (e.clientX >= r.left && e.clientX < r.right) { newDay = i; break; }
    }

    if (newDay !== parseInt(el.parentElement.dataset.day)) {
      cols[newDay].appendChild(el);
    }

    el.classList.add('dragging');
    el.style.top = newSlot * SLOT_H + 'px';
    state.drag.curSlot = newSlot;
    state.drag.curDay  = newDay;

  } else {
    const deltaSlots = Math.round(dy / SLOT_H);
    let newDur = state.drag.origDur + deltaSlots;
    newDur = Math.max(MIN_DUR, Math.min(TOTAL_S - state.drag.origStart, newDur));
    el.style.height = newDur * SLOT_H - 2 + 'px';
    state.drag.curDur = newDur;
  }
}

function onDragUp() {
  document.removeEventListener('mousemove', onDragMove);
  document.removeEventListener('mouseup',   onDragUp);

  if (!state.drag) { state.pending = null; return; }

  const el = document.querySelector(`.block[data-id="${state.drag.id}"]`);
  el?.classList.remove('dragging');

  if (state.drag.started) {
    let patch;
    if (state.drag.type === 'move') {
      patch = { startSlot: state.drag.curSlot, day: state.drag.curDay };
    } else {
      patch = { dur: Math.max(MIN_DUR, state.drag.curDur) };
    }
    patchBlock(state.drag.id, patch);
    rerenderBlock(state.drag.id);
    const _dragId = state.drag.id;
    updateBlockAPI(_dragId, patch).then(() => {
      const _b = findBlock(_dragId);
      if (_b?.calendarEventId) updateBlockInCalendar(_b.calendarEventId, _dragId).catch(() => {});
    }).catch(() => {});
  } else {
    if (state.pending && !state.pending.moved) {
      selectBlockExternal(state.pending.id);
    }
  }

  state.drag = null;
  state.pending = null;
}

// ══════════════════════════════════════════════════════════
// DRAG-TO-CREATE ON EMPTY COLUMN
// ══════════════════════════════════════════════════════════
function onColMousedown(e) {
  const target = e.target;
  if (!target.classList.contains('day-col') && !target.classList.contains('slot-line')) return;
  if (e.button !== 0) return;
  e.preventDefault();

  const col = e.currentTarget;
  const day = parseInt(col.dataset.day);
  const colRect = col.getBoundingClientRect();
  const startSlot = Math.max(0, Math.min(TOTAL_S - 2, Math.floor((e.clientY - colRect.top) / SLOT_H)));

  const preview = document.createElement('div');
  preview.className = 'block-preview';
  preview.style.top    = startSlot * SLOT_H + 'px';
  preview.style.height = 2 * SLOT_H - 2 + 'px';
  preview.innerHTML    = '<svg width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> Nou bloc';
  col.appendChild(preview);

  state.draw = { day, col, colRect, startSlot, endSlot: startSlot + 2, preview };

  document.addEventListener('mousemove', onDrawMousemove, { passive: true });
  document.addEventListener('mouseup',   onDrawMouseup);
}

function onDrawMousemove(e) {
  if (!state.draw) return;
  const { colRect, startSlot, preview } = state.draw;

  const rawSlot = Math.floor((e.clientY - colRect.top) / SLOT_H);
  const endSlot = Math.max(startSlot + 2, Math.min(TOTAL_S, rawSlot + 1));

  state.draw.endSlot = endSlot;
  const dur = endSlot - startSlot;
  preview.style.height = dur * SLOT_H - 2 + 'px';

  const startT = slotToHHMM(startSlot);
  const endT   = slotToHHMM(endSlot);
  preview.innerHTML = dur >= 3
    ? `<span style="display:flex;flex-direction:column;align-items:center;gap:2px"><b>+</b><span style="font-family:'JetBrains Mono',monospace;font-size:9px;opacity:0.8">${startT}–${endT}</span></span>`
    : '<svg width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>';
}

function onDrawMouseup() {
  document.removeEventListener('mousemove', onDrawMousemove);
  document.removeEventListener('mouseup',   onDrawMouseup);
  if (!state.draw) return;

  const { day, col, startSlot, endSlot, preview } = state.draw;
  col.removeChild(preview);
  state.draw = null;

  openCreateModalExternal(day, startSlot, endSlot);
}

// ══════════════════════════════════════════════════════════
// CURRENT TIME INDICATOR
// ══════════════════════════════════════════════════════════
export function updateNowLine() {
  document.querySelectorAll('.now-line').forEach(e => e.remove());

  const now = new Date();
  const todayMidnight = new Date(now); todayMidnight.setHours(0,0,0,0);

  for (let i = 0; i < 7; i++) {
    const d = new Date(state.currentMonday);
    d.setDate(d.getDate() + i);
    if (d.getTime() !== todayMidnight.getTime()) continue;

    const minOfDay = now.getHours() * 60 + now.getMinutes();
    const minOffset = minOfDay - START_H * 60;
    if (minOffset < 0 || minOffset > (END_H - START_H) * 60) break;

    const top = (minOffset / 15) * SLOT_H;
    const col = document.querySelector(`.day-col[data-day="${i}"]`);
    if (!col) break;

    const line = document.createElement('div');
    line.className = 'now-line';
    line.style.top = top + 'px';
    col.appendChild(line);
    break;
  }

  clearTimeout(updateNowLine._t);
  updateNowLine._t = setTimeout(updateNowLine, 60000);
}

// ══════════════════════════════════════════════════════════
// CALENDAR EVENTS RENDERING
// ══════════════════════════════════════════════════════════
export function renderCalendarEvents() {
  document.querySelectorAll('.cal-event-block').forEach(e => e.remove());

  // Group timed ghosts by day so overlaps split into columns per day.
  const byDay = {};
  state.calendarEvents.forEach(ev => {
    if (ev.startSlot == null) return;
    (byDay[ev.day] ||= []).push(ev);
  });

  Object.entries(byDay).forEach(([day, evs]) => {
    const col = document.querySelector(`.day-col[data-day="${day}"]`);
    if (!col) return;

    const layout = assignOverlapColumns(
      evs.map(e => ({ id: e.id, startSlot: e.startSlot, dur: e.dur }))
    );

    evs.forEach(ev => {
      const { col: colIndex = 0, nCols = 1 } = layout[ev.id] || {};

      // Normalize 3-digit hex, then derive uniform-weight colors (option C).
      let baseHex = ev.color || '#4285f4';
      if (baseHex.length === 4) {
        baseHex = '#' + baseHex[1]+baseHex[1]+baseHex[2]+baseHex[2]+baseHex[3]+baseHex[3];
      }
      const rgb       = hexToRgb(baseHex) || hexToRgb('#4285f4');
      const textColor = hexToUniformDarkText(baseHex);
      const bgColor   = `rgba(${rgb.r},${rgb.g},${rgb.b},0.04)`;
      const bdColor   = `rgba(${rgb.r},${rgb.g},${rgb.b},0.40)`;

      const startT = ev.startTime || slotToHHMM(ev.startSlot);
      const endT   = ev.endTime   || slotToHHMM(ev.startSlot + (ev.dur || 0));

      const el = document.createElement('div');
      el.className = 'cal-event-block ghost';
      el.dataset.calId = ev.id;
      el.dataset.col   = colIndex;
      el.dataset.ncols = nCols;
      el.style.cssText =
        `top:${ev.startSlot * SLOT_H}px;height:${ev.dur * SLOT_H - 2}px;` +
        `background:${bgColor};border-color:${bdColor};`;
      el.innerHTML = `
        <div class="cal-event-title" style="color:${textColor}">
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="4" width="18" height="17" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/></svg>
          ${esc(ev.title)}
        </div>
        ${ev.dur >= 2 ? `<div class="cal-event-time" style="color:${textColor}">${startT} – ${endT}</div>` : ''}`;
      el.title = ev.title;
      col.appendChild(el);
    });
  });

  applyAllEventRails();
}

// ── Event rail: GCal events that overlap a block move to the right edge of
// the column and the block narrows, so neither title is drawn over the other.
const RAIL_W = 38; // % of the column width reserved for the rail

function applyEventRails(day) {
  const col = document.querySelector(`.day-col[data-day="${day}"]`);
  if (!col) return;

  const toBox = x => ({ id: String(x.id), startSlot: x.startSlot, dur: x.dur || 0 });
  const blocks = getWeekBlocks().filter(b => b.day == day).map(toBox);
  const events = state.calendarEvents
    .filter(ev => ev.day == day && ev.startSlot != null)
    .map(toBox);
  const { blockIds, eventIds } = findBlockEventOverlaps(blocks, events);

  col.querySelectorAll('.block').forEach(el => {
    el.classList.toggle('has-rail', blockIds.has(el.dataset.id));
  });

  markQuietDay(day, blocks.length + events.length === 0);

  col.querySelectorAll('.cal-event-block').forEach(el => {
    const inRail   = eventIds.has(el.dataset.calId);
    const colIndex = Number(el.dataset.col) || 0;
    const nCols    = Number(el.dataset.ncols) || 1;
    el.classList.toggle('in-rail', inRail);

    if (!inRail && nCols === 1) {
      el.style.left = '5px'; el.style.right = '5px'; el.style.width = '';
      return;
    }
    // Split the available region (whole column or rail) between overlapping events.
    const start = inRail ? 100 - RAIL_W : 0;
    const w     = (inRail ? RAIL_W : 100) / nCols;
    el.style.left  = `calc(${start + colIndex * w}% + 3px)`;
    el.style.width = `calc(${w}% - 6px)`;
    el.style.right = 'auto';
  });
}

function applyAllEventRails() {
  for (let d = 0; d < 7; d++) applyEventRails(d);
}

// Dissabte/diumenge sense res planificat s'estrenyen (CSS, només vista setmana)
// perquè els dies laborables guanyin ample.
function markQuietDay(day, nothingTimed) {
  if (Number(day) < 5) return;
  const hasAllDay = state.allDayEvents.some(ev => {
    const s = ev.startDay ?? ev.day ?? 0;
    return s <= day && day <= (ev.endDay ?? s);
  });
  const quiet = nothingTimed && !hasAllDay;
  const els = document.querySelectorAll(
    `#cal-header .day-hcell[data-day="${day}"], #allday-lane .allday-cell[data-day="${day}"], .day-col[data-day="${day}"]`
  );
  let changed = false;
  els.forEach(el => {
    if (el.classList.contains('is-quiet') !== quiet) changed = true;
    el.classList.toggle('is-quiet', quiet);
  });
  if (changed) renderAllDayEvents();
}

// ══════════════════════════════════════════════════════════
// Cross-module call bridges (set by main.js at init time)
// Avoids circular imports while keeping grid.js self-contained.
// ══════════════════════════════════════════════════════════
let openCreateModalExternal = () => {};
let openEditModalExternal   = () => {};
let openChoiceModalExternal = () => {};
let openGCalEventModalExternal = () => {};
let renderSidebarExternal   = () => {};
let selectBlockExternal     = () => {};
let deselectBlockExternal   = () => {};
let deleteTemplateExternal  = () => {};
let _toast                  = () => {};

export function registerGridCallbacks({
  openCreateModal, openEditModal, openChoiceModal, openGCalEventModal,
  renderSidebar, selectBlock, deselectBlock, deleteTemplate, toast,
}) {
  openCreateModalExternal    = openCreateModal;
  openEditModalExternal      = openEditModal;
  openChoiceModalExternal    = openChoiceModal;
  openGCalEventModalExternal = openGCalEventModal;
  renderSidebarExternal      = renderSidebar;
  selectBlockExternal        = selectBlock;
  deselectBlockExternal      = deselectBlock;
  deleteTemplateExternal     = deleteTemplate;
  _toast                     = toast;
}
